import { getActuatorGroupStatus } from '@/api/ems_energy_manager'
import { snakeCase } from 'lodash'
import moment from 'moment-timezone'

const state = () => ({
  actuatorGroupStatus: {} // { [agId]: { type, data: {...}, interval } }
})
const CHARGING_MODES = ['costOptimal', 'alwaysFast', 'fastOnce', 'excessPower', 'excessPowerPlus', 'noCharging']
const COST_OPTIMAL_STATUSES = ['unknownChargingPower', 'unknownPrices', 'costOptimal', 'chargingDone']
const MAX_END_OF_CHARGE_HORIZON_DAYS = 3

const getters = {
  getActuatorGroupStatusDataKeys: (state) => (agId) => Object.keys(state.actuatorGroupStatus[agId]?.data || {}),
  getChargePointChargingStatus: (state) => {
    return ({ key, agId }) => state.actuatorGroupStatus[agId]?.data?.[key]?.status || ''
  },
  getChargePointNameData: (state) => {
    return ({ key, agId }) => ({
      physicalDeviceNumber: state.actuatorGroupStatus[agId]?.data?.[key]?.physicalDevice?.number || 0,
      logicalDeviceString: `${state.actuatorGroupStatus[agId]?.data?.[key]?.chargePoint?.iec?.logicalDeviceName}.${state.actuatorGroupStatus[agId]?.data?.[key]?.chargePoint?.iec?.iecNodePostfix || 0}`
    })
  },
  getChargePointChargingMode: (state) => {
    return ({ key, agId }) => {
      const chargePointData = state.actuatorGroupStatus[agId]?.data?.[key]
      if (!chargePointData) return ''

      for (const mode of CHARGING_MODES) {
        if (mode in chargePointData) return snakeCase(mode)
      }
      return ''
    }
  },
  getChargePointCostOptimalStatus: (state) => {
    return ({ key, agId }) => {
      const chargePointData = state.actuatorGroupStatus[agId]?.data?.[key]
      if (!chargePointData || !chargePointData.costOptimal) return null

      const requestedWs = chargePointData.costOptimal.requestedEnergyWattSeconds || 0
      const remainingWs = chargePointData.costOptimal.remainingEnergyWattSeconds || 0
      const requestedEnergyWh = Number((requestedWs / 3_600).toFixed(0))
      const remainingEnergyWh = Number((remainingWs / 3_600).toFixed(0))
      const chargedPerc =
        requestedEnergyWh > 0 ? Math.min(100, Math.round((1 - remainingEnergyWh / requestedEnergyWh) * 100)) : 0

      return {
        mode: costOptimalMode(chargePointData.costOptimal),
        chargedPerc,
        requestedEnergyWh,
        remainingEnergyWh,
        endOfCharge: endOfCharge(chargePointData)
      }
    }
  },
  getChargePointFallbackMode: (state) => {
    return ({ key, agId }) => snakeCase(state.actuatorGroupStatus[agId]?.data?.[key]?.fastOnce || '')
  },
  getRequestErrorCount: (state) => (agId) => state.actuatorGroupStatus[agId]?.requestErrorCount || 0
}

const mutations = {
  SET_ACTUATOR_GROUP_STATUS_DATA(state, { agId, actuatorGroupStatusData }) {
    state.actuatorGroupStatus[agId] ||= {}
    state.actuatorGroupStatus[agId].data = actuatorGroupStatusData
  },
  SET_ACTUATOR_GROUP_STATUS_INTERVAL_AND_TYPE(state, { intervalId, agId, type }) {
    state.actuatorGroupStatus[agId] ||= {}
    if (state.actuatorGroupStatus[agId].interval) {
      clearInterval(state.actuatorGroupStatus[agId].interval)
    }
    state.actuatorGroupStatus[agId].type = type
    state.actuatorGroupStatus[agId].interval = intervalId
    state.actuatorGroupStatus[agId].requestErrorCount = 0
  },
  CLEAR_ACTUATOR_GROUP_STATUS(state, { agId }) {
    const entry = state.actuatorGroupStatus[agId]
    if (entry?.interval) {
      clearInterval(entry.interval)
    }
    delete state.actuatorGroupStatus[agId]
  },
  ADD_REQUEST_ERROR(state, { agId }) {
    if (!state.actuatorGroupStatus[agId]) return

    state.actuatorGroupStatus[agId].requestErrorCount++
  },
  RESET_REQUEST_ERROR_COUNT(state, { agId }) {
    if (!state.actuatorGroupStatus[agId]) return

    state.actuatorGroupStatus[agId].requestErrorCount = 0
  }
}

const actions = {
  getChargePointData: ({ commit, getters }, { ctxt, emsId, agId }) => {
    return getActuatorGroupStatus({
      ctxt,
      emsId,
      id: agId,
      storeType: 'charge_point_manager'
    })
      .then((response) => {
        const chargePointStatuses = response.payload.data.attributes.chargePointManager.chargePointsStatus || []
        const data = chargePointStatuses.reduce((acc, cps) => {
          const id = createChargePointId(cps)
          cps.status ||= 'NOT_PLUGGED'
          acc[id] = cps
          return acc
        }, {})
        commit('SET_ACTUATOR_GROUP_STATUS_DATA', {
          agId,
          actuatorGroupStatusData: data
        })
        commit('RESET_REQUEST_ERROR_COUNT', { agId })
      })
      .catch((err) => {
        commit('ADD_REQUEST_ERROR', { agId })
        if (getters.getRequestErrorCount(agId) > 10) {
          commit(
            'notifications/PUSH_TOAST',
            {
              title: err.title,
              content: err.detail
            },
            { root: true }
          )
          commit('RESET_REQUEST_ERROR_COUNT', { agId })
        }
        return null
      })
  },
  pollChargingStatuses: ({ state, commit, dispatch }, { ctxt, emsId, agId, pollingInterval = 5000 }) => {
    if (!state.actuatorGroupStatus[agId]?.interval) {
      dispatch('getChargePointData', { ctxt, emsId, agId })
      const intervalId = setInterval(() => {
        dispatch('getChargePointData', { ctxt, emsId, agId })
      }, pollingInterval)
      commit('SET_ACTUATOR_GROUP_STATUS_INTERVAL_AND_TYPE', { intervalId, agId, type: 'chargePointManager' })

      return intervalId
    }
  },
  stopPollChargingStatuses: ({ state, commit }, { agId }) => {
    if (state.actuatorGroupStatus[agId]?.interval) {
      commit('CLEAR_ACTUATOR_GROUP_STATUS', { agId })
    }
  }
}

const createChargePointId = (chargePointStatus) =>
  `${chargePointStatus.chargePoint?.iec?.logicalDeviceName}.${chargePointStatus.chargePoint?.iec?.iecNodePostfix || 0}`

const endOfCharge = (chargePointData) => {
  if (!chargePointData?.costOptimal) return null
  if (chargePointData.status === 'PROBING') return null

  const co = chargePointData.costOptimal
  const timestamp = 'costOptimal' in co ? co.costOptimal : co.unknownPrices

  const plannedEndOfCharge = moment.unix(timestamp)
  if (!plannedEndOfCharge.isValid()) return null
  if (!plannedEndOfCharge.isBetween(moment(), moment().add(MAX_END_OF_CHARGE_HORIZON_DAYS, 'days'))) return null

  const requestedEndOfCharge = co.requestedEndOfCharge
  const msAfterRequested =
    requestedEndOfCharge > 0
      ? moment.duration(plannedEndOfCharge - moment.unix(requestedEndOfCharge), 'milliseconds')
      : 0
  return { time: plannedEndOfCharge, msAfterRequested }
}

function costOptimalMode(costOptimal) {
  if (!costOptimal) return ''
  for (const status of COST_OPTIMAL_STATUSES) {
    if (status in costOptimal) return snakeCase(status)
  }
  return ''
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}
